
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import Cookies from "js-cookie";
import { io } from "socket.io-client";
import { convertAudioBlobToMp3 } from "../../../utils/audioToMp3";

// ---------- Group Voice Recording Modal ----------
export default function VoiceSendModal({ open, onClose, groupId }) {
  const token = Cookies.get("auth_token");
  const modalRef = useRef();
  const [time, setTime] = useState("00:00");
  const [seconds, setSeconds] = useState(0);

  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const socketRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const [segmentChunks, setSegmentChunks] = useState([]);

  // Timer logic
  useEffect(() => {
    let interval;
    if (recording) {
      interval = setInterval(() => {
        setSeconds((prev) => prev + 1);
      }, 1000);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [recording]);

  // Format time display
  useEffect(() => {
    const m = Math.floor(seconds / 60)
      .toString()
      .padStart(2, "0");
    const s = (seconds % 60).toString().padStart(2, "0");
    setTime(`${m}:${s}`);
  }, [seconds]);

  // Stop recording when modal closes
  useEffect(() => {
    if (!open) {
      // Modal is closing, stop recording and clean up
      try {
        if (recorderRef.current && recorderRef.current.state !== "inactive") {
          recorderRef.current.stop();
        }
      } catch {}
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      setRecording(false);
      setSegmentChunks([]);
      setSeconds(0);
      setTime("00:00");
    }
  }, [open]);

  // Initialize socket connection when modal opens
  useEffect(() => {
    if (open && !socketRef.current) {
      const socketUrl = "https://phonebookplus.app";
      const socketPath = "/api/socket.io/";

      const socket = io(socketUrl, {
        path: socketPath,
        transports: ["polling"],
        query: { token },
        upgrade: false,
        reconnection: true,
      });

      socketRef.current = socket;

      socket.on("connect", () => {
        // Join group when socket connects
        if (groupId) {
          socket.emit("join_group", { token, group_id: groupId });
        }
      });

      socket.on("connect_error", (err) => {
        });
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [open, token, groupId]);

  // Auto-start recording when modal opens
  useEffect(() => {
    if (open && !recording) {
      // Small delay to ensure modal is rendered
      const timer = setTimeout(() => {
        handleStart();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Cleanup on unmount: stop recording and release tracks
  useEffect(() => {
    return () => {
      try {
        if (recorderRef.current && recorderRef.current.state !== "inactive") {
          recorderRef.current.stop();
        }
      } catch { }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, []);

  if (!open) return null;

  const handleStart = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";

      const recorder = new MediaRecorder(stream, { mimeType });
      recorderRef.current = recorder;
      setSeconds(0);
      setSegmentChunks([]);

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          setSegmentChunks((prev) => [...prev, event.data]);
        }
      };

      recorder.onstart = () => setRecording(true);
      recorder.onstop = () => setRecording(false);

      // Start emitting chunks every 500ms for quick send
      recorder.start(500);
      toast.success("Recording started");
    } catch (e) {
      toast.error("Microphone access denied.");
    }
  };

  const handleStop = () => {
    try {
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.stop();
      }
    } catch { }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const handleClose = () => {
    handleStop();
    setSegmentChunks([]);
    setSeconds(0);
    setTime("00:00");
    onClose();
  };

  const handleDelete = () => {
    handleStop();
    setSegmentChunks([]);
    setSeconds(0);
    setTime("00:00");
    toast.dismiss();
    toast("Recording deleted successfully.");
    onClose();
  };

  const buildSegmentBlob = () => {
    if (!segmentChunks || segmentChunks.length === 0) return null;
    return new Blob(segmentChunks, { type: "audio/webm" });
  };

  const handleSend = async () => {
    // Stop recording immediately to capture only the current duration
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setRecording(false);

    // Wait a brief moment for final chunks to be captured
    await new Promise(resolve => setTimeout(resolve, 100));

    const blob = buildSegmentBlob();
    if (!blob || blob.size === 0) {
      toast.error("Nothing to send yet. Please speak a bit more.");
      return;
    }

    if (!groupId) {
      toast.error("No group selected");
      return;
    }

    if (!socketRef.current || !socketRef.current.connected) {
      toast.error("Not connected. Please try again.");
      return;
    }

    try {
      const loadingId = toast.loading("Sending voice message to group...");
      const mp3Blob = await convertAudioBlobToMp3(blob);

      // Convert blob to array buffer
      const arrayBuffer = await mp3Blob.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);

      const filename = `voice_${Date.now()}.mp3`;
      const payload = {
        token,
        group_id: groupId,
        filename: filename,
        mime_type: "audio/mpeg",
        file: bytes,
      };

      socketRef.current.emit("send_group_file", payload, (ack) => {
        toast.dismiss(loadingId);

        if (ack && ack.error) {
          toast.error(ack.error || "Failed to send voice message");
        } else {
          toast.success("Voice message sent to group!");

          // Reset state and close immediately
          setSegmentChunks([]);
          setSeconds(0);
          setTime("00:00");
          onClose();
        }
      });
    } catch (err) {
      toast.error("Failed to send voice message");
    }
  };

  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
      <div
        ref={modalRef}
        className="relative bg-white rounded-[40px] shadow-xl px-8 pt-7 pb-4 flex flex-col items-center w-80"
      >
        <button
          type="button"
          onClick={handleClose}
          className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 transition hover:bg-gray-200"
          aria-label="Close"
        >
          <img src="../chatbox/close.svg" alt="close" className="h-4 w-4" />
        </button>
        <div className="mb-4">
          <div className="mx-auto rounded-full p-3">
            <button
              type="button"
              onClick={() => {
                if (recording) {
                  handleStop();
                } else {
                  handleStart();
                }
              }}
              aria-pressed={recording}
              className="rounded-full"
            >
              <img
                src={recording ? "../chatscreen/pause.svg" : "../chatscreen/main-mic.svg"}
                alt={recording ? "pause" : "mic"}
                className={`w-28 h-28 ${recording ? "animate-pulse" : ""}`}
              />
            </button>
          </div>
        </div>

        <div className="text-lg font-bold text-[#000000] mb-3">
          {time}
        </div>

        {/* 🗑️ Delete & Send Buttons */}
        <div className="flex gap-8 mt-5">
          <button
            className="bg-gray-100 rounded-lg p-2 hover:bg-gray-200"
            onClick={handleDelete}
          >
            <img
              src="../chatscreen/trash.svg"
              alt="delete"
              className="w-10 h-6"
            />
          </button>
          <button
            className="bg-gray-100 rounded-lg p-2 hover:bg-gray-200 disabled:opacity-40"
            onClick={handleSend}
            disabled={segmentChunks.length === 0}
          >
            <img
              src="../chatscreen/send-arrow.svg"
              alt="send"
              className="w-10 h-10"
            />
          </button>
        </div>
      </div>
    </div>
  );
}

